import {EventType, Flow, FlowEditHistoryEntry, FlowEditHistoryStage, FlowEditOrderReason} from '../models/flow';

/**
 * Utilidades para trabajar con el flujo de un Service.
 *
 * Desde que el Superadmin puede editar el flujo con SOs vivas, una etapa
 * retirada no se borra: queda con isActive = false para que las SOs que pasaron
 * por ella sigan resolviendo su historial. Eso parte el flujo en dos lecturas:
 *
 * - Flujo ACTIVO: lo que se edita y por donde avanzan las SOs -> activeEvents()
 * - Flujo HISTORICO: incluye las retiradas, solo para renderizar el pasado de
 *   una SO -> flow.events sin filtrar
 *
 * Todo calculo de secuencia (siguiente estado, estado previo, estado final)
 * tiene que correr sobre el flujo ACTIVO. Si no, una etapa retirada en el medio
 * deja un hueco y los calculos basados en sequence dan resultados incorrectos.
 */

/** Una etapa cuenta como activa salvo que el backend la marque explicitamente como retirada. */
export function isActiveEventType(eventType: EventType): boolean {
  return eventType.isActive !== false;
}

/** Etapa retirada del flujo: sobrevive solo para resolver el historial de las SOs. */
export function isRetiredEventType(eventType: EventType): boolean {
  return eventType.isActive === false;
}

/** Etapas vigentes del flujo, ordenadas por secuencia. No muta el flow recibido. */
export function activeEvents(flow?: Flow): EventType[] {
  return sortedEvents(flow?.events).filter(isActiveEventType);
}

/** Todas las etapas (vigentes y retiradas) ordenadas por secuencia. No muta el array recibido. */
export function sortedEvents(events?: EventType[]): EventType[] {
  return [...(events || [])].sort((a, b) => a.sequence - b.sequence);
}

/**
 * Etapa anterior a la dada dentro del flujo activo.
 *
 * No se puede buscar por `sequence - 1`: al retirar una etapa del medio queda un
 * hueco en la numeracion y esa busqueda devuelve undefined, lo que hace que
 * hasPreviousEventOccurred() de por cumplida la etapa previa y relaje el control
 * de transiciones. Se busca por posicion dentro del flujo activo ordenado.
 */
export function previousActiveEvent(events: EventType[], current: EventType): EventType | undefined {
  const active = sortedEvents(events).filter(isActiveEventType);
  const index = active.findIndex(e => e.id === current.id);
  return index > 0 ? active[index - 1] : undefined;
}

/** Etapa final del flujo activo (la de mayor secuencia entre las vigentes). */
export function finalActiveEvent(events?: EventType[]): EventType | undefined {
  const active = sortedEvents(events).filter(isActiveEventType);
  return active.length ? active[active.length - 1] : undefined;
}

/**
 * Id temporal de una etapa que todavia no existe en el servidor.
 *
 * Negativo para distinguirla de las reales, y PEQUENO a proposito: el backend lo
 * recibe como entero de 32 bits, asi que el `Date.now() * -1` que se usaba antes
 * (~1,79 billones) se sale del rango y hace que rechace la edicion entera.
 */
let lastTempEventTypeId = 0;

export function nextTempEventTypeId(): number {
  return --lastTempEventTypeId;
}

/** Texto de cada motivo por el que una edicion cambio el estado de una SO. */
export const FLOW_EDIT_ORDER_REASON_TEXT: Record<FlowEditOrderReason, string> = {
  ReorderCrossesState: 'Volvió al inicio: el nuevo orden dejaba una etapa completada detrás de otra pendiente',
  HistoryNotPrefix: 'Volvió al inicio: su historial ya no encajaba en el flujo',
  StageHidden: 'Su etapa se ocultó del flujo',
  NewStageAhead: 'Se añadió una etapa por delante',
};

/** «A», «A» y «B», «A», «B» y «C» */
function formatStageNames(stages: FlowEditHistoryStage[]): string {
  const names = stages.map(stage => `«${stage.name}»`);
  return names.length > 1
    ? `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`
    : names[0];
}

function describeStages(verb: string, stages: FlowEditHistoryStage[]): string | null {
  if (!stages.length) return null;
  return `${verb} ${stages.length === 1 ? 'la etapa' : 'las etapas'} ${formatStageNames(stages)}`;
}

/**
 * Resumen de una edicion del flujo en lenguaje de usuario, para el historial:
 * «Añadió la etapa «X» · Ocultó la etapa «Y» · 12 órdenes volvieron al inicio».
 */
export function describeFlowEdit(entry: FlowEditHistoryEntry): string {
  const parts = [
    describeStages('Añadió', entry.stagesAdded),
    describeStages('Ocultó', entry.stagesHidden),
    describeStages('Modificó', entry.stagesUpdated),
    entry.reordered ? 'Cambió el orden de las etapas' : null,
    entry.ordersReset
      ? `${entry.ordersReset} ${entry.ordersReset === 1 ? 'orden volvió' : 'órdenes volvieron'} al inicio`
      : null,
    entry.ordersReprojected
      ? `${entry.ordersReprojected} ${entry.ordersReprojected === 1 ? 'orden cambió' : 'órdenes cambiaron'} de etapa`
      : null,
  ].filter((part): part is string => !!part);

  return parts.length ? parts.join(' · ') : 'Sin cambios en las etapas';
}
